import { useState } from "react";
import { toast } from "sonner";
import {
  Network, Plus, ArrowRight, ArrowDownUp, ChevronUp, ChevronDown, Share2,
  CheckCircle2, Building2, MoveRight, Inbox, Send,
} from "lucide-react";
import {
  Button, Card, CardBody, Badge, PageHeader, EmptyState, Loading, AccessDenied,
  Alert, Field, TextInput, Select, Modal, ConfirmDialog, Tabs,
} from "@/components/redvolt/kit";
import {
  REL_TYPE_LABEL, REL_STATUS, orgLabel, useLoader, apiError, rankBetween,
} from "@/app/federation/common";

const StatusBadge = ({ status }) => {
  const s = REL_STATUS[status] || { label: status, variant: "neutral" };
  return <Badge variant={s.variant}>{s.label}</Badge>;
};

/* --------------------------- Request modal ------------------------------ */
const RequestModal = ({ api, tenantId, types, onClose, onDone }) => {
  const [target, setTarget] = useState("");
  const [type, setType] = useState(types[0]?.key || "coalition_peer");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!target.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/relationships`, {
        target_tenant_id: target.trim(), relationship_type_key: type,
        display_label: label.trim() || null,
      });
      toast.success("Connection request sent");
      onDone();
      onClose();
    } catch (e) { apiError(e, "Could not send request"); }
    finally { setBusy(false); }
  };
  return (
    <Modal
      open onClose={onClose} testid="request-relationship-modal"
      title="Request a connection"
      description="Connecting lets two organizations relate. It does not share any data on its own."
      footer={<>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy || !target.trim()} data-testid="submit-relationship-request">
          {busy ? "Sending…" : "Send request"}
        </Button>
      </>}
    >
      <div className="space-y-4">
        <Field label="Organization ID" htmlFor="rel-target" hint="Ask the other organization for their Organization ID.">
          <TextInput id="rel-target" data-testid="rel-target-input" value={target}
            onChange={(e) => setTarget(e.target.value)} placeholder="Paste the organization's ID" />
        </Field>
        <Field label="Relationship type" htmlFor="rel-type">
          <Select id="rel-type" data-testid="rel-type-select" value={type} onChange={(e) => setType(e.target.value)}>
            {types.map((t) => <option key={t.key} value={t.key}>{t.label}{t.hierarchical ? " (structure)" : ""}</option>)}
          </Select>
        </Field>
        <Field label="Label (optional)" htmlFor="rel-label" hint="A friendly name to recognise this connection.">
          <TextInput id="rel-label" data-testid="rel-label-input" value={label}
            onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Chester County GOP" />
        </Field>
        <Alert variant="info">Connecting does not share information. You choose what to share afterwards.</Alert>
      </div>
    </Modal>
  );
};

/* --------------------------- Relationship card -------------------------- */
const RelCard = ({ rel, caps, onAction, onNavigate }) => {
  const name = orgLabel(rel);
  const typeLabel = REL_TYPE_LABEL[rel.type] || rel.type;
  const canManage = caps.includes("relationship:manage");
  const canApprove = caps.includes("relationship:approve");
  const incomingPending = rel.status === "pending_acceptance" && rel.direction === "incoming" && !rel.is_move;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`relationship-${rel.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-accent text-primary"><Building2 className="h-4 w-4" /></span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{name}</p>
          <p className="text-xs text-slate-500">{typeLabel} · {rel.direction === "incoming" ? "They reached out" : "You reached out"}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <StatusBadge status={rel.status} />
        {incomingPending && canApprove && (
          <>
            <Button size="sm" onClick={() => onAction(rel, "accept")} data-testid={`accept-relationship-${rel.id}`}>Accept</Button>
            <Button size="sm" variant="outline" onClick={() => onAction(rel, "reject")} data-testid={`reject-relationship-${rel.id}`}>Decline</Button>
          </>
        )}
        {rel.status === "active" && (
          <Button size="sm" variant="outline" onClick={() => onNavigate("sharing")} data-testid={`manage-sharing-${rel.id}`}>
            <Share2 className="h-4 w-4" /> Sharing
          </Button>
        )}
        {rel.status === "active" && canManage && (
          <>
            <Button size="sm" variant="ghost" onClick={() => onAction(rel, "suspend")} data-testid={`suspend-relationship-${rel.id}`}>Pause</Button>
            <Button size="sm" variant="destructive" onClick={() => onAction(rel, "terminate")} data-testid={`terminate-relationship-${rel.id}`}>End</Button>
          </>
        )}
        {rel.status === "suspended" && canManage && (
          <>
            <Button size="sm" variant="outline" onClick={() => onAction(rel, "restore")} data-testid={`restore-relationship-${rel.id}`}>Resume</Button>
            <Button size="sm" variant="destructive" onClick={() => onAction(rel, "terminate")}>End</Button>
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------ Move modal ------------------------------ */
const MoveModal = ({ api, tenantId, rel, types, onClose, onDone }) => {
  const [dest, setDest] = useState("");
  const [type, setType] = useState(rel.type);
  const [busy, setBusy] = useState(false);
  const hierTypes = types.filter((t) => t.hierarchical);
  const submit = async () => {
    if (!dest.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/structure/move`, {
        old_relationship_id: rel.id, new_parent_tenant_id: dest.trim(), relationship_type_key: type,
      });
      toast.success("Move requested — the destination must accept it");
      onDone(); onClose();
    } catch (e) { apiError(e, "Could not request the move"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} testid="move-modal" title="Move organization"
      description="Propose moving under a new parent organization."
      footer={<>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy || !dest.trim()} data-testid="submit-move">
          {busy ? "Requesting…" : "Request move"}
        </Button>
      </>}>
      <div className="space-y-4">
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          Currently: <span className="font-semibold">{orgLabel(rel)}</span>
        </div>
        <Field label="New parent Organization ID" htmlFor="move-dest">
          <TextInput id="move-dest" data-testid="move-dest-input" value={dest} onChange={(e) => setDest(e.target.value)} placeholder="Paste the destination organization's ID" />
        </Field>
        <Field label="Relationship type" htmlFor="move-type">
          <Select id="move-type" value={type} onChange={(e) => setType(e.target.value)}>
            {hierTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </Select>
        </Field>
        <Alert variant="warning" title="Sharing does not move">
          Existing sharing permissions do not move with the organization. The destination must accept, and your current parent connection will end.
        </Alert>
      </div>
    </Modal>
  );
};

/* ------------------------------ Structure ------------------------------- */
const StructureTab = ({ api, tenantId, rels, caps, types, reload }) => {
  const [moveRel, setMoveRel] = useState(null);
  const canReorder = caps.includes("structure:reorder");
  const canMove = caps.includes("structure:manage");
  const hier = rels
    .filter((r) => r.status === "active" && (types.find((t) => t.key === r.type)?.hierarchical))
    .sort((a, b) => (a.sort_rank || "").localeCompare(b.sort_rank || ""));

  const reorder = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= hier.length) return;
    const moved = hier[idx];
    const prev = dir < 0 ? hier[j - 1] : hier[j];
    const next = dir < 0 ? hier[j] : hier[j + 1];
    const rank = rankBetween(prev?.sort_rank, next?.sort_rank);
    try {
      await api.post(`/tenants/${tenantId}/relationships/${moved.id}/reorder`, { sort_rank: rank });
      reload();
    } catch (e) { apiError(e, "Could not reorder"); }
  };

  if (hier.length === 0) {
    return <EmptyState icon={ArrowDownUp} title="No structural connections"
      description="Structural connections (like ‘Area of’ or ‘Chapter of’) let you organize how your organizations relate. They still don’t share data automatically." />;
  }
  return (
    <div className="space-y-2" data-testid="structure-list">
      {hier.map((r, i) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`structure-${r.id}`}>
          <div className="flex items-center gap-3 min-w-0">
            {canReorder && (
              <div className="flex flex-col">
                <button className="text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={i === 0} onClick={() => reorder(i, -1)} aria-label="Move up" data-testid={`reorder-up-${r.id}`}><ChevronUp className="h-4 w-4" /></button>
                <button className="text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={i === hier.length - 1} onClick={() => reorder(i, 1)} aria-label="Move down" data-testid={`reorder-down-${r.id}`}><ChevronDown className="h-4 w-4" /></button>
              </div>
            )}
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-accent text-primary"><Building2 className="h-4 w-4" /></span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">{orgLabel(r)}</p>
              <p className="text-xs text-slate-500">{REL_TYPE_LABEL[r.type] || r.type} · {r.is_child ? "Your organization is the child" : "Your organization is the parent"}</p>
            </div>
          </div>
          {canMove && r.is_child && (
            <Button size="sm" variant="outline" onClick={() => setMoveRel(r)} data-testid={`move-${r.id}`}>
              <MoveRight className="h-4 w-4" /> Move
            </Button>
          )}
        </div>
      ))}
      {moveRel && <MoveModal api={api} tenantId={tenantId} rel={moveRel} types={types} onClose={() => setMoveRel(null)} onDone={reload} />}
    </div>
  );
};

/* ------------------------------- Page ----------------------------------- */
export default function ConnectionsPage({ api, active, caps, onNavigate }) {
  const tenantId = active.tenant_id;
  const [tab, setTab] = useState("connected");
  const [showRequest, setShowRequest] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [justConnected, setJustConnected] = useState(false);

  const canView = caps.includes("relationship:view");
  const canManage = caps.includes("relationship:manage");

  const relsQ = useLoader(async () => (await api.get(`/tenants/${tenantId}/relationships`)).data.relationships, [tenantId]);
  const typesQ = useLoader(async () => (await api.get(`/reference/relationship-types`)).data.types, []);
  const rels = relsQ.data || [];
  const types = typesQ.data || [];

  const doAction = async (rel, action) => {
    const needsConfirm = ["terminate", "reject", "suspend"].includes(action);
    if (needsConfirm && !confirm) {
      setConfirm({ rel, action });
      return;
    }
    setBusy(true);
    try {
      if (action === "accept") {
        await api.post(`/tenants/${tenantId}/relationships/${rel.id}/accept`);
        setJustConnected(true);
        toast.success("Connected");
      } else {
        await api.post(`/tenants/${tenantId}/relationships/${rel.id}/${action}`);
        toast.success("Done");
      }
      relsQ.reload();
    } catch (e) { apiError(e); }
    finally { setBusy(false); setConfirm(null); }
  };

  if (!canView) return <AccessDenied message="Your role doesn’t include viewing connected organizations." />;

  const connected = rels.filter((r) => r.status === "active" || r.status === "suspended");
  const requests = rels.filter((r) => r.status === "pending_acceptance");
  const incoming = requests.filter((r) => r.direction === "incoming");
  const outgoing = requests.filter((r) => r.direction === "outgoing");

  const tabs = [
    { key: "connected", label: "Connected", count: connected.length },
    { key: "requests", label: "Requests", count: requests.length },
    { key: "structure", label: "Structure" },
  ];

  return (
    <div className="space-y-6" data-testid="connections-page">
      <PageHeader
        title="Connected Organizations"
        subtitle="Relationships link organizations. Sharing is separate and always your choice."
        action={canManage && (
          <Button onClick={() => setShowRequest(true)} data-testid="request-relationship-button">
            <Plus className="h-4 w-4" /> Request connection
          </Button>
        )}
      />

      {justConnected && (
        <Alert variant="success" title="Connected">
          <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> No information is being shared unless you explicitly choose to share it.</div>
        </Alert>
      )}

      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {relsQ.loading ? <Loading label="Loading connections…" /> : (
        <>
          {tab === "connected" && (
            connected.length === 0 ? (
              <EmptyState icon={Network} title="No connected organizations yet"
                description="Organizational relationships let you formally relate to other organizations. Connecting never shares data on its own."
                action={canManage && <Button size="sm" onClick={() => setShowRequest(true)}>Request a connection</Button>} />
            ) : (
              <div className="space-y-2">
                {connected.map((r) => <RelCard key={r.id} rel={r} caps={caps} onAction={doAction} onNavigate={onNavigate} />)}
              </div>
            )
          )}

          {tab === "requests" && (
            requests.length === 0 ? (
              <EmptyState icon={Inbox} title="No pending requests"
                description="Requests you send or receive will appear here for review." />
            ) : (
              <div className="space-y-5">
                <div>
                  <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><Inbox className="h-4 w-4" /> Incoming</p>
                  {incoming.length === 0 ? <p className="text-sm text-slate-400">Nothing waiting on you.</p> :
                    <div className="space-y-2">{incoming.map((r) => <RelCard key={r.id} rel={r} caps={caps} onAction={doAction} onNavigate={onNavigate} />)}</div>}
                </div>
                <div>
                  <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><Send className="h-4 w-4" /> Sent</p>
                  {outgoing.length === 0 ? <p className="text-sm text-slate-400">You haven’t sent any requests.</p> :
                    <div className="space-y-2">{outgoing.map((r) => <RelCard key={r.id} rel={r} caps={caps} onAction={doAction} onNavigate={onNavigate} />)}</div>}
                </div>
              </div>
            )
          )}

          {tab === "structure" && (
            <Card><CardBody>
              <StructureTab api={api} tenantId={tenantId} rels={rels} caps={caps} types={types} reload={relsQ.reload} />
            </CardBody></Card>
          )}
        </>
      )}

      {showRequest && <RequestModal api={api} tenantId={tenantId} types={types} onClose={() => setShowRequest(false)} onDone={relsQ.reload} />}

      <ConfirmDialog
        open={!!confirm}
        onCancel={() => setConfirm(null)}
        onConfirm={() => doAction(confirm.rel, confirm.action)}
        busy={busy}
        title={confirm?.action === "terminate" ? "End this connection?" : confirm?.action === "suspend" ? "Pause this connection?" : "Decline this request?"}
        confirmLabel={confirm?.action === "terminate" ? "End connection" : confirm?.action === "suspend" ? "Pause" : "Decline"}
      >
        {confirm?.action === "terminate" && "Ending the connection immediately stops any sharing that relied on it. Existing activity history is kept."}
        {confirm?.action === "suspend" && "Pausing temporarily disables sharing that relies on this connection. You can resume later."}
        {confirm?.action === "reject" && "This request will be declined. The other organization can send a new request later."}
      </ConfirmDialog>
    </div>
  );
}
